import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LOGO_URL } from "../lib/brand";
import { LogOut, Inbox, RefreshCw, BarChart3, Users, CheckCircle2, XCircle, Trash2, Mail, UserPlus, Clock, Sparkles, AlertCircle, Cake, Link2, FastForward, Edit2, Check, X as XIcon, FileSignature, Download, Search, AlertTriangle, GitMerge, Loader2, KeyRound, Lock, Stethoscope, Unplug } from "lucide-react";
import AdminTopNav from "../components/AdminTopNav";
import SupportDelayBroadcastButton from "../components/admin/SupportDelayBroadcastButton";
import { getAuthHeaders, isAuthed, isPartner, logout, loginUrl, getAdminToken } from "../lib/adminAuth";
import { BACKEND_URL } from "../lib/apiBase";
import MergeCustomersModal from "../components/admin/MergeCustomersModal";
import { StatusBadge, ContractBadge, formatDate } from "../components/admin/CustomerBadges";

const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  pending: { label: "Wartet auf Freigabe", color: "amber" },
  approved: { label: "Freigeschaltet", color: "emerald" },
  rejected: { label: "Abgelehnt", color: "rose" },
};

const formatBirthdate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
};

export default function AdminOnboardingCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editingMt5, setEditingMt5] = useState(null); // customer id currently editing MT5
  const [mt5Draft, setMt5Draft] = useState("");
  const [editingEmail, setEditingEmail] = useState(null); // customer id currently editing email
  const [emailDraft, setEmailDraft] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  // Fuzzy-duplicate cluster panel (collapsible).
  const [duplicates, setDuplicates] = useState(null); // null | {clusters, scanned}
  const [duplicatesBusy, setDuplicatesBusy] = useState(false);
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  // Bulk-merge modal — opened from a customer-customer pair inside the cluster.
  const [mergeModal, setMergeModal] = useState(null); // null | {a: customer, b: customer}
  const [mergePrimaryId, setMergePrimaryId] = useState(null);
  const [mergeBusy, setMergeBusy] = useState(false);
  const navigate = useNavigate();
  const partnerMode = isPartner();

  const saveEmail = async (c) => {
    const next = (emailDraft || "").trim().toLowerCase();
    if (!next) {
      toast.error("E-Mail darf nicht leer sein.");
      return;
    }
    if (next === (c.email || "").toLowerCase()) {
      setEditingEmail(null);
      return;
    }
    if (!window.confirm(
      `E-Mail von "${c.email}" auf "${next}" ändern?\n\n` +
      `→ Der Kunde meldet sich danach mit der neuen E-Mail an.\n` +
      `→ Alle verknüpften Mail-Anfragen werden mit-umgehängt.`
    )) return;
    setEmailBusy(true);
    try {
      await axios.patch(
        `${API}/admin/customers/${c.id}`,
        { email: next },
        { headers: getAuthHeaders() },
      );
      toast.success(`E-Mail aktualisiert auf ${next}.`);
      setEditingEmail(null);
      await fetchCustomers();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Speichern fehlgeschlagen.");
    } finally {
      setEmailBusy(false);
    }
  };

  const fetchDuplicates = async () => {
    setDuplicatesBusy(true);
    try {
      const res = await axios.get(`${API}/admin/maintenance/email-duplicates`, { headers: getAuthHeaders() });
      setDuplicates(res.data);
      setDuplicatesOpen(true);
      if ((res.data?.clusters || []).length === 0) {
        toast.success("Keine Duplikat-Cluster gefunden — Datenbank ist sauber. ");
      } else {
        toast.message(`${res.data.clusters.length} mögliche Duplikat-Cluster gefunden.`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Duplikat-Scan fehlgeschlagen.");
    } finally {
      setDuplicatesBusy(false);
    }
  };

  // Open the merge modal for two customer records inside a cluster.
  const openMerge = (a, b) => {
    const ca = customers.find((c) => c.id === a.id);
    const cb = customers.find((c) => c.id === b.id);
    if (!ca || !cb) {
      toast.error("Kunden-Daten nicht geladen. Bitte aktualisieren.");
      return;
    }
    setMergeModal({ a: ca, b: cb });
    // Auto-pick the more "complete" one as default primary.
    const score = (c) => (
      (c.status === "approved" ? 100 : 0) +
      (c.contract?.signed_at ? 50 : 0) +
      (c.contract?.waived ? 25 : 0) +
      (c.mt5_account ? 10 : 0) +
      ((c.onboarding_progress?.video1_checks || []).filter(Boolean).length) +
      ((c.onboarding_progress?.video2_checks || []).filter(Boolean).length)
    );
    setMergePrimaryId(score(ca) >= score(cb) ? ca.id : cb.id);
  };

  const runMerge = async () => {
    if (!mergeModal || !mergePrimaryId) return;
    const { a, b } = mergeModal;
    const primary = a.id === mergePrimaryId ? a : b;
    const duplicate = a.id === mergePrimaryId ? b : a;
    if (!window.confirm(
      `WIRKLICH zusammenführen?\n\n` +
      `→ Behalten: ${primary.email}\n` +
      `→ Löschen:  ${duplicate.email}\n\n` +
      `Alle Mail-Anfragen + Leads von "${duplicate.email}" werden auf "${primary.email}" umgehängt.\n` +
      `Onboarding-Fortschritt, MT5-Daten und Vertrag werden bestmöglich verschmolzen.\n\n` +
      `Diese Aktion ist nicht rückgängig zu machen.`
    )) return;
    setMergeBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/maintenance/merge-customers`,
        { primary_id: primary.id, duplicate_id: duplicate.id },
        { headers: getAuthHeaders() },
      );
      const d = res.data || {};
      toast.success(
        `Zusammengeführt: ${d.deleted_duplicate_email} → ${d.primary_email}. ` +
        `${d.contacts_reattached || 0} Anfragen + ${d.leads_reattached || 0} Leads umgehängt.`
      );
      setMergeModal(null);
      setMergePrimaryId(null);
      await fetchCustomers();
      await fetchDuplicates();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Merge fehlgeschlagen.");
    } finally {
      setMergeBusy(false);
    }
  };

  const saveMt5 = async (c) => {
    const cleaned = (mt5Draft || "").replace(/[^0-9]/g, "");
    if (cleaned === (c.mt5_account || "")) {
      setEditingMt5(null);
      return;
    }
    setBusy(c.id);
    try {
      await axios.patch(
        `${API}/admin/customers/${c.id}`,
        { mt5_account: cleaned },
        { headers: getAuthHeaders() },
      );
      toast.success(cleaned ? `MT5-Nummer auf ${cleaned} gesetzt.` : "MT5-Nummer geleert.");
      setEditingMt5(null);
      await fetchCustomers();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Speichern fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  const fetchCustomers = useCallback(async () => {
    if (!isAuthed()) {
      return navigate(loginUrl());
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/customers`, { headers: getAuthHeaders() });
      setCustomers(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        const target = loginUrl();
        logout();
        navigate(target);
      } else {
        toast.error("Konnte Kunden nicht laden.");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const approve = async (c) => {
    if (!c.contract?.signed_at && !c.contract?.waived && !window.confirm(
      `${c.first_name} ${c.last_name} freischalten, obwohl noch KEIN unterschriebener Dienstleistungsvertrag vorliegt?\n\nDer Kunde sollte den Vertrag normalerweise vorher unterschreiben.`
    )) return;
    setBusy(c.id);
    try {
      await axios.post(`${API}/admin/customers/${c.id}/approve`, {}, { headers: getAuthHeaders() });
      toast.success(`${c.first_name} freigeschaltet — Mail wurde versendet.`);
      await fetchCustomers();
    } catch { toast.error("Freischaltung fehlgeschlagen."); }
    finally { setBusy(null); }
  };

  const openContractPdf = async (c) => {
    // Open a blank tab SYNCHRONOUSLY (within the click gesture) — otherwise
    // the browser blocks the popup once we go async with axios.
    const popup = window.open("about:blank", "_blank");
    try {
      const res = await axios.get(`${API}/admin/customers/${c.id}/contract.pdf`, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      if (popup && !popup.closed) {
        popup.location.href = url;
      } else {
        // Popup was blocked — force a download instead.
        const a = document.createElement("a");
        a.href = url;
        a.download = `Vertrag-${c.first_name || "kunde"}-${c.last_name || c.id.slice(0, 6)}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      if (popup && !popup.closed) popup.close();
      toast.error("Vertrag konnte nicht geöffnet werden.");
    }
  };

  const toggleContractWaiver = async (c) => {
    const next = !c.contract?.waived;
    const msg = next
      ? `${c.first_name} ${c.last_name} muss KEINEN Dienstleistungsvertrag mehr unterschreiben? (z. B. bereits offline unterschrieben — Bestandskunde)`
      : `Vertrags-Befreiung für ${c.first_name} ${c.last_name} wieder aufheben? Der Kunde wird beim nächsten Login zur Unterschrift geleitet.`;
    if (!window.confirm(msg)) return;
    setBusy(c.id);
    try {
      await axios.patch(`${API}/admin/customers/${c.id}`, { contract_waived: next }, { headers: getAuthHeaders() });
      toast.success(next ? "Vertragspflicht aufgehoben." : "Vertragspflicht reaktiviert.");
      await fetchCustomers();
    } catch { toast.error("Aktion fehlgeschlagen."); }
    finally { setBusy(null); }
  };

  const reject = async (c) => {
    if (!window.confirm(`${c.first_name} ${c.last_name} wirklich ablehnen?`)) return;
    setBusy(c.id);
    try {
      await axios.post(`${API}/admin/customers/${c.id}/reject`, {}, { headers: getAuthHeaders() });
      toast.success("Abgelehnt.");
      await fetchCustomers();
    } catch { toast.error("Ablehnung fehlgeschlagen."); }
    finally { setBusy(null); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Kunde ${c.email} unwiderruflich löschen?`)) return;
    setBusy(c.id);
    try {
      await axios.delete(`${API}/admin/customers/${c.id}`, { headers: getAuthHeaders() });
      toast.success("Kunde gelöscht.");
      await fetchCustomers();
    } catch { toast.error("Löschen fehlgeschlagen."); }
    finally { setBusy(null); }
  };

  const resetProgress = async (c) => {
    if (!window.confirm(`Onboarding-Fortschritt für ${c.email} zurücksetzen? Alle Häkchen werden entfernt, MT5-Nummer geleert.`)) return;
    setBusy(c.id);
    try {
      await axios.post(`${API}/admin/customers/${c.id}/reset-progress`, {}, { headers: getAuthHeaders() });
      toast.success("Fortschritt zurückgesetzt — Kunde kann jetzt wieder speichern.");
      await fetchCustomers();
    } catch { toast.error("Reset fehlgeschlagen."); }
    finally { setBusy(null); }
  };

  const resetMyFxBook = async (c) => {
    if (!window.confirm(
      `MyFxBook-Verbindung von "${c.first_name} ${c.last_name}" (${c.email}) komplett trennen?\n\n` +
      `→ Gespeicherte MyFxBook-Zugangsdaten werden gelöscht.\n` +
      `→ Alle synchronisierten Snapshots + Trades werden entfernt.\n` +
      `→ Kunde muss sich neu verbinden. Danach läuft der Initial-Sync sauber.`
    )) return;
    setBusy(c.id);
    try {
      const res = await axios.post(`${API}/admin/customers/${c.id}/myfxbook/reset`, {}, { headers: getAuthHeaders() });
      const d = res.data || {};
      toast.success(`MyFxBook getrennt · ${d.snapshots_deleted} Snapshots + ${d.trades_deleted} Trades gelöscht.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "MyFxBook-Reset fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  const triggerPasswordReset = async (c) => {
    if (!window.confirm(
      `Passwort-Reset-Mail an "${c.first_name} ${c.last_name}" (${c.email}) senden?\n\n` +
      `→ Der Kunde erhält einen Link, mit dem er sein Passwort selbst neu setzen kann.\n` +
      `→ Link ist 24 Stunden gültig und nur einmal verwendbar.\n` +
      `→ Du siehst das neue Passwort nicht — Kunde wählt es selbst.`
    )) return;
    setBusy(c.id);
    try {
      const res = await axios.post(`${API}/admin/customers/${c.id}/trigger-password-reset`, {}, { headers: getAuthHeaders() });
      const d = res.data || {};
      if (d.email_sent) {
        toast.success(`Reset-Mail an ${d.email} versendet. Link gilt 24h, einmalig verwendbar.`);
      } else {
        toast.warning(
          `Mail-Versand fehlgeschlagen — Link wurde in die Zwischenablage kopiert: ${d.reset_link}`,
          { duration: 20000 },
        );
        try { await navigator.clipboard.writeText(d.reset_link || ""); } catch { /* clipboard may be blocked */ }
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Reset fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  const setPasswordDirect = async (c) => {
    // Auto-generate a German-friendly memorable password (no $/!/special chars
    // that get mangled in WhatsApp/SMS or via iOS Safari window.prompt).
    const adjektive = ["Lila", "Roter", "Heller", "Goldner", "Blauer", "Grüner", "Mutiger", "Stiller", "Weiser"];
    const tiere = ["Tiger", "Falke", "Bär", "Adler", "Fuchs", "Wolf", "Hirsch", "Habicht", "Otter"];
    const adj = adjektive[Math.floor(Math.random() * adjektive.length)];
    const tier = tiere[Math.floor(Math.random() * tiere.length)];
    const num = Math.floor(100 + Math.random() * 900);
    const suggested = `${adj}-${tier}-${num}`;

    const pw = window.prompt(
      `Neues Passwort für "${c.first_name} ${c.last_name}" (${c.email}):\n\n` +
      `→ Mindestens 8 Zeichen, keine Sonderzeichen empfohlen\n` +
      `→ Wird automatisch in die Zwischenablage kopiert\n` +
      `→ Sende es per WhatsApp/Telefon an den Kunden\n\n` +
      `Vorschlag: ${suggested}`,
      suggested,
    );
    if (pw == null) return;
    const trimmed = (pw || "").trim();
    if (trimmed !== pw) {
      toast.warning("Whitespace wurde automatisch entfernt — bitte nochmals bestätigen.");
    }
    if (trimmed.length < 8) {
      toast.error("Passwort braucht mindestens 8 Zeichen.");
      return;
    }
    setBusy(c.id);
    try {
      const res = await axios.post(
        `${API}/admin/customers/${c.id}/set-password`,
        { new_password: trimmed },
        { headers: getAuthHeaders() },
      );
      const d = res.data || {};
      try { await navigator.clipboard.writeText(trimmed); } catch { /* ignore */ }
      // Surface diagnostics — if any check fails, warn the admin LOUDLY
      // instead of silently sending them off to broken-login land.
      if (d.verify_ok === false || d.login_path_ok === false || d.duplicate_email_count > 1) {
        const issues = [];
        if (d.verify_ok === false) issues.push("Hash wurde nicht gespeichert");
        if (d.login_path_ok === false) issues.push(`Login findet einen ANDEREN Datensatz (id mismatch)`);
        if (d.duplicate_email_count > 1) issues.push(`${d.duplicate_email_count} Kunden mit dieser Email gefunden!`);
        toast.error(
          `⚠️ Passwort wurde geschrieben, ABER der Login schlägt fehl: ${issues.join(" · ")}. Bitte Kundendaten prüfen — evtl. doppelter Account.`,
          { duration: 30000 },
        );
      } else {
        toast.success(
          `✓ Passwort für ${c.email} gesetzt + in Zwischenablage kopiert.\n` +
          `Self-Test OK — Login funktioniert mit "${trimmed}".`,
          { duration: 15000 },
        );
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Passwort-Set fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  const diagnoseAndRepair = async (c) => {
    if (!window.confirm(
      `Login-Diagnose & Datenreparatur für "${c.first_name} ${c.last_name}" (${c.email})?\n\n` +
      `→ Prüft Schritt für Schritt warum Login fehlschlägt\n` +
      `→ Behebt automatisch datetime / NaN / korrupte Felder\n` +
      `→ Login-Hash bleibt unverändert`
    )) return;
    setBusy(c.id);
    try {
      // Step 1: diagnose
      const emailEnc = encodeURIComponent(c.email);
      const diag = await axios.get(
        `${API}/admin/customers/by-email/${emailEnc}/diagnose`,
        { headers: getAuthHeaders() },
      );
      const steps = diag.data?.steps || {};
      const failing = Object.entries(steps).filter(([, v]) => v?.ok === false);
      // Step 2: repair (always run — idempotent)
      const rep = await axios.post(
        `${API}/admin/customers/by-email/${emailEnc}/repair`,
        {},
        { headers: getAuthHeaders() },
      );

      if (failing.length === 0) {
        toast.success(
          `✓ Diagnose OK — keine Probleme gefunden. ${rep.data?.fields_processed || 0} Felder zur Sicherheit saniert.`,
          { duration: 10000 },
        );
      } else {
        const list = failing.map(([k, v]) => `${k}: ${v.exception || v.reason || "fehlgeschlagen"}`).join(" · ");
        toast.success(
          `✓ ${failing.length} Problem(e) gefunden + repariert: ${list}`,
          { duration: 20000 },
        );
      }
      console.log("[diagnose]", diag.data);
      console.log("[repair]", rep.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Diagnose fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  const toggleSkipOnboarding = async (c) => {
    const wantSkip = !c.skip_onboarding;
    const alreadyWaived = !!c.contract?.waived || !!c.contract?.signed_at;
    const willAutoWaive = wantSkip && !alreadyWaived;
    const msg = wantSkip
      ? `Onboarding für "${c.first_name} ${c.last_name}" überspringen?\n\nDer Kunde sieht KEINE Videos/Checkliste mehr, sondern direkt einen Button zum Dashboard.\n\n(Sinnvoll für Partner, die bereits auf RoboForex onboardet sind.)${willAutoWaive ? "\n\n→ Vertragspflicht wird gleichzeitig aufgehoben (Bestandskunde)." : ""}`
      : `Onboarding-Skip für "${c.first_name} ${c.last_name}" aufheben?\n\nDer Kunde muss die komplette Checkliste wieder durchlaufen. Fortschritt wird zurückgesetzt.`;
    if (!window.confirm(msg)) return;
    setBusy(c.id);
    try {
      const endpoint = wantSkip ? "skip-onboarding" : "unskip-onboarding";
      await axios.post(`${API}/admin/customers/${c.id}/${endpoint}`, {}, { headers: getAuthHeaders() });
      // When activating skip, auto-waive the contract for legacy/existing
      // customers who haven't signed digitally — they've usually completed
      // the paperwork offline.
      if (willAutoWaive) {
        try {
          await axios.patch(`${API}/admin/customers/${c.id}`, { contract_waived: true }, { headers: getAuthHeaders() });
        } catch { /* non-fatal — admin can toggle separately */ }
      }
      toast.success(
        wantSkip
          ? (willAutoWaive
              ? "Onboarding übersprungen + Vertragspflicht aufgehoben."
              : "Onboarding übersprungen — Kunde sieht jetzt den Dashboard-Shortcut.")
          : "Onboarding wieder aktiviert."
      );
      await fetchCustomers();
    } catch { toast.error("Aktion fehlgeschlagen."); }
    finally { setBusy(null); }
  };

  const assignPartner = async (c) => {
    const current = c.partner_slug || "";
    const input = window.prompt(
      `Partner-Slug für ${c.first_name} ${c.last_name} eintragen (z.B. "pascalschwab"). Leer lassen, um zu entfernen.`,
      current
    );
    if (input === null) return; // cancelled
    const slug = (input || "").trim().toLowerCase();
    setBusy(c.id);
    try {
      const res = await axios.patch(
        `${API}/admin/customers/${c.id}`,
        { partner_slug: slug || null },
        { headers: getAuthHeaders() }
      );
      const name = res.data?.partner_name || res.data?.partner_slug;
      toast.success(name ? `Partner zugewiesen: ${name}` : "Partner entfernt.");
      await fetchCustomers();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Partner-Zuweisung fehlgeschlagen.";
      toast.error(detail);
    } finally { setBusy(null); }
  };

  const filtered = customers.filter((c) => {
    // 1) Status / completion filter
    if (filter === "incomplete") {
      // Approved customers who still have to finish the onboarding videos.
      const progress = c.onboarding_progress || {};
      const allDone = !!progress.all_completed;
      if (c.status !== "approved" || allDone) return false;
    } else if (filter !== "all" && c.status !== filter) {
      return false;
    }
    // 2) Free-text search across name + email + MT5 number
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const haystack = [
      c.first_name, c.last_name, c.email,
      `${c.first_name || ""} ${c.last_name || ""}`,
      c.mt5_account,
    ].filter(Boolean).join(" ").toLowerCase();
    return q.split(/\s+/).every((tok) => haystack.includes(tok));
  });
  const counts = {
    all: customers.length,
    pending: customers.filter((c) => c.status === "pending").length,
    approved: customers.filter((c) => c.status === "approved").length,
    rejected: customers.filter((c) => c.status === "rejected").length,
    // "Onboarding offen" = approved customers whose video1+video2 progress
    // is not yet marked all_completed. Excludes pending/rejected (where 0/13
    // is the expected resting state).
    incomplete: customers.filter((c) => {
      if (c.status !== "approved") return false;
      return !(c.onboarding_progress || {}).all_completed;
    }).length,
  };

  const doLogout = () => { const target = loginUrl(); logout(); navigate(target); };

  return (
    <div className="min-h-screen bg-[var(--al-bg)]" data-testid="admin-onboarding-customers">
      <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src={LOGO_URL} alt="Alphalane" className="h-8 w-8 sm:h-9 sm:w-9 object-contain flex-shrink-0" />
            <div className="font-display min-w-0">
              <div className="font-semibold text-[14px] sm:text-[15px] truncate">
                <span className="al-grad-text">ALPHALANE</span>
                <span className="hidden sm:inline text-slate-500 text-xs ml-2 font-medium uppercase tracking-widest">Admin</span>
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <button onClick={fetchCustomers} className="al-cta-ghost !py-2 !px-2 sm:!px-3 text-sm" data-testid="oc-refresh" aria-label="Aktualisieren">
              <RefreshCw className="h-4 w-4" /> <span className="hidden sm:inline">Aktualisieren</span>
            </button>
            <button onClick={doLogout} className="al-cta-ghost !py-2 !px-2 sm:!px-3 text-sm" data-testid="oc-logout" aria-label="Logout">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <AdminTopNav />

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <span className="al-tag">Onboarding Kunden</span>
            <h1 className="mt-3 font-display font-bold text-2xl sm:text-3xl lg:text-4xl">
              Registrierungen <span className="al-grad-text">({filtered.length})</span>
            </h1>
            <p className="mt-2 text-sm text-slate-600 max-w-xl">Neue Anmeldungen prüfen und freigeben. Bei Freigabe wird automatisch eine Bestätigungsmail an den Kunden versendet.</p>
          </div>
          {!partnerMode && (
            <SupportDelayBroadcastButton
              apiBase={API}
              token={getAdminToken()}
            />
          )}
        </div>

        {/* Duplicate-scan toolbar */}
        {!partnerMode && (
          <div className="mb-4 al-card p-4" data-testid="oc-duplicates-toolbar">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <div className="font-display font-semibold text-sm text-slate-900 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" /> Mögliche E-Mail-Duplikate
                </div>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                  Findet Tippfehler-Cluster über alle Kunden, Anfragen und Leads
                  (z. B. „aron@gmai.com" vs. „aron@gmail.com").
                </p>
              </div>
              <button
                type="button"
                onClick={fetchDuplicates}
                disabled={duplicatesBusy}
                data-testid="oc-duplicates-scan"
                className="al-cta-ghost !py-2 !px-3 text-sm disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${duplicatesBusy ? "animate-spin" : ""}`} />
                {duplicatesBusy ? "Scanne…" : (duplicates ? "Neu scannen" : "Scan starten")}
              </button>
              {duplicates && (
                <button
                  type="button"
                  onClick={() => setDuplicatesOpen((v) => !v)}
                  data-testid="oc-duplicates-toggle"
                  className="al-cta-ghost !py-2 !px-3 text-sm"
                >
                  {duplicatesOpen ? "Verbergen" : "Anzeigen"}
                </button>
              )}
            </div>
            {duplicatesOpen && duplicates && (
              <div className="mt-4" data-testid="oc-duplicates-panel">
                {duplicates.clusters.length === 0 ? (
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800">
                    Keine Duplikate gefunden. Gescannt: {duplicates.scanned.customers} Kunden · {duplicates.scanned.contacts} Anfragen · {duplicates.scanned.leads} Leads.
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">
                      {duplicates.clusters.length} Cluster · {duplicates.scanned.customers}/{duplicates.scanned.contacts}/{duplicates.scanned.leads} (Kunden/Anfragen/Leads)
                    </div>
                    {duplicates.clusters.map((cl, idx) => {
                      const customerRecs = cl.records.filter((r) => r.kind === "customer");
                      return (
                      <div key={`${cl.key}-${idx}`} data-testid={`oc-duplicate-cluster-${idx}`} className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                          <div className="font-mono-tab text-[13px] font-bold text-amber-900 break-all">
                            {cl.key}
                          </div>
                          {customerRecs.length >= 2 && (
                            <button
                              type="button"
                              onClick={() => openMerge(customerRecs[0], customerRecs[1])}
                              data-testid={`oc-duplicate-merge-${idx}`}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-violet-600 text-white text-[11px] font-bold hover:bg-violet-700 transition-colors"
                              title="Zwei Kunden-Accounts zu einem zusammenführen"
                            >
                              <GitMerge className="h-3 w-3" /> Merge
                            </button>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          {cl.records.map((r, j) => (
                            <div key={`${r.kind}-${r.id}-${j}`} className="flex items-center gap-2 flex-wrap text-[12px]">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${
                                r.kind === "customer" ? "bg-emerald-100 text-emerald-800" :
                                r.kind === "contact" ? "bg-violet-100 text-violet-800" :
                                "bg-slate-100 text-slate-700"
                              }`}>
                                {r.kind === "customer" ? "Kunde" : r.kind === "contact" ? "Anfrage" : "Lead"}
                              </span>
                              <span className="font-mono-tab text-slate-800 break-all">{r.email}</span>
                              {r.distance > 0 && (
                                <span className="text-[10px] font-bold rounded-full px-1.5 py-0.5 bg-amber-200 text-amber-900" title={`Levenshtein-Distanz zu "${cl.key}"`}>
                                  Δ{r.distance}
                                </span>
                              )}
                              {r.name && <span className="text-slate-600">· {r.name}</span>}
                              {r.kind === "customer" && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const target = customers.find((c) => c.id === r.id);
                                    if (target) {
                                      setEmailDraft(target.email || "");
                                      setEditingEmail(target.id);
                                      window.scrollTo({ top: 0, behavior: "smooth" });
                                      toast.info(`Editor für ${target.first_name} ${target.last_name} ist oben in der Liste geöffnet.`);
                                    }
                                  }}
                                  data-testid={`oc-duplicate-edit-${r.id}`}
                                  className="ml-auto text-[11px] font-semibold text-[var(--al-purple)] hover:underline inline-flex items-center gap-1"
                                >
                                  <Edit2 className="h-3 w-3" /> Bearbeiten
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Filter pills + search */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <div className="flex flex-wrap gap-2" data-testid="oc-filter">
            {[
              { id: "all", label: "Alle" },
              { id: "pending", label: "Wartet" },
              { id: "approved", label: "Freigeschaltet" },
              { id: "rejected", label: "Abgelehnt" },
              { id: "incomplete", label: "Onboarding offen" },
            ].map((f) => {
              const active = filter === f.id;
              return (
                <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`oc-filter-${f.id}`}
                  className={`px-4 py-1.5 rounded-full border text-sm font-display font-semibold transition-all flex items-center gap-2 ${active ? "bg-slate-900 text-white border-transparent" : "border-[var(--al-line)] text-slate-600 hover:text-slate-900"}`}>
                  {f.label}
                  <span className={`text-[11px] font-mono-tab font-bold tabular-nums ${active ? "opacity-90" : "text-slate-400"}`}>{counts[f.id]}</span>
                </button>
              );
            })}
          </div>
          <div className="relative flex-1 min-w-[220px] sm:max-w-xs ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, E-Mail oder MT5-Nummer suchen…"
              data-testid="oc-search"
              className="w-full pl-9 pr-9 py-1.5 rounded-full border border-[var(--al-line)] bg-white text-sm placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                data-testid="oc-search-clear"
                aria-label="Suche zurücksetzen"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center"
              >
                <XIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="al-card p-12 text-center text-slate-500">Lade…</div>
        ) : filtered.length === 0 ? (
          <div className="al-card p-12 text-center" data-testid="oc-empty">
            <UserPlus className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="mt-4 text-slate-600">Keine Kunden in dieser Ansicht.</p>
          </div>
        ) : (
          <div className="al-card overflow-hidden" data-testid="oc-list">
            <div className="divide-y divide-[var(--al-line)]">
              {filtered.map((c) => {
                const status = STATUS_LABELS[c.status] || { label: c.status, color: "slate" };
                const progress = c.onboarding_progress || {};
                const v1 = (progress.video1_checks || []).filter(Boolean).length;
                const v2 = (progress.video2_checks || []).filter(Boolean).length;
                const total = v1 + v2;
                const TOTAL_CHECKS = 13; // 8 (video 1) + 5 (video 2)
                const allDone = !!progress.all_completed;
                return (
                  <div key={c.id} className="px-5 py-5 grid grid-cols-1 lg:grid-cols-12 gap-4 items-center" data-testid={`oc-row-${c.id}`}>
                    <div className="lg:col-span-4 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display font-semibold text-slate-900">{c.first_name} {c.last_name}</div>
                        <StatusBadge status={c.status} />
                        <ContractBadge contract={c.contract} />
                      </div>
                      {editingEmail === c.id ? (
                        <div className="mt-1 flex items-center gap-1.5" data-testid={`oc-email-editor-${c.id}`}>
                          <Mail className="h-3 w-3 text-slate-400" />
                          <input
                            type="email"
                            value={emailDraft}
                            onChange={(e) => setEmailDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveEmail(c);
                              if (e.key === "Escape") setEditingEmail(null);
                            }}
                            data-testid={`oc-email-input-${c.id}`}
                            placeholder="email@domain.com"
                            autoFocus
                            className="font-mono-tab text-xs px-2 py-1 rounded border border-violet-200 focus:outline-none focus:border-[var(--al-purple)] flex-1 min-w-[180px]"
                          />
                          <button
                            onClick={() => saveEmail(c)}
                            disabled={emailBusy}
                            data-testid={`oc-email-save-${c.id}`}
                            className="p-1 rounded text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                            title="Speichern (Enter)"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => setEditingEmail(null)}
                            data-testid={`oc-email-cancel-${c.id}`}
                            className="p-1 rounded text-slate-500 hover:bg-slate-100"
                            title="Abbrechen (Esc)"
                          >
                            <XIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => { setEmailDraft(c.email || ""); setEditingEmail(c.id); }}
                          data-testid={`oc-email-edit-${c.id}`}
                          className="text-xs text-slate-500 truncate mt-1 inline-flex items-center gap-1.5 hover:text-[var(--al-purple)] group"
                          title="E-Mail bearbeiten (z. B. Typo korrigieren)"
                        >
                          <Mail className="h-3 w-3" /> {c.email}
                          <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      )}
                      <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1.5">
                        <Clock className="h-3 w-3" /> Registriert: {formatDate(c.created_at)}
                      </div>
                      {c.birthdate && (
                        <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1.5" data-testid={`oc-birthdate-${c.id}`}>
                          <Cake className="h-3 w-3 text-pink-500" /> Geburtstag: <span className="font-mono-tab">{formatBirthdate(c.birthdate)}</span>
                        </div>
                      )}
                      {c.contract?.signed_at ? (
                        <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-2.5 py-1.5 text-[11px] text-emerald-900 flex items-center gap-2 flex-wrap" data-testid={`oc-contract-info-${c.id}`}>
                          <FileSignature className="h-3 w-3 shrink-0" />
                          <span><strong>Vertrag</strong> · {formatDate(c.contract.signed_at)}</span>
                          {c.contract.startbetrag != null && (
                            <span className="font-mono-tab">{Number(c.contract.startbetrag).toLocaleString("de-CH").replace(/\u202F/g, "'")} USD</span>
                          )}
                          <span className="text-emerald-700/70">{c.contract.method === "upload" ? "(hochgeladen)" : "(digital)"}</span>
                          <button
                            onClick={() => openContractPdf(c)}
                            data-testid={`oc-contract-open-${c.id}`}
                            className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 hover:underline"
                          >
                            <Download className="h-3 w-3" /> öffnen
                          </button>
                        </div>
                      ) : c.contract?.waived ? (
                        <div className="mt-2 rounded-lg border border-sky-200 bg-sky-50/60 px-2.5 py-1.5 text-[11px] text-sky-900 flex items-center gap-2 flex-wrap" data-testid={`oc-contract-waived-${c.id}`}>
                          <FileSignature className="h-3 w-3 shrink-0" />
                          <span><strong>Vertrag-Befreiung</strong> · seit {formatDate(c.contract.waived_at)}</span>
                          <button
                            onClick={() => toggleContractWaiver(c)}
                            disabled={busy === c.id}
                            data-testid={`oc-contract-waive-off-${c.id}`}
                            className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-sky-800 hover:underline disabled:opacity-50"
                          >
                            Wieder aktivieren
                          </button>
                        </div>
                      ) : (
                        <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50/60 px-2.5 py-1.5 text-[11px] text-rose-900 flex items-center gap-2 flex-wrap" data-testid={`oc-contract-missing-${c.id}`}>
                          <FileSignature className="h-3 w-3 shrink-0" />
                          <span><strong>Vertrag offen</strong> — wartet auf Kunden-Unterschrift</span>
                          <button
                            onClick={() => toggleContractWaiver(c)}
                            disabled={busy === c.id}
                            data-testid={`oc-contract-waive-on-${c.id}`}
                            className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-rose-800 hover:underline disabled:opacity-50"
                            title="Vertragspflicht überspringen (z. B. Bestandskunde, der bereits offline unterschrieben hat)"
                          >
                            Vertrag-Pflicht überspringen
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="lg:col-span-3">
                      <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Partner</div>
                      <div className="mt-1 text-sm font-medium text-slate-900">
                        {c.partner_name || c.partner_slug || <span className="text-slate-400">—</span>}
                      </div>
                      {c.partner_slug && (
                        <div className="text-[11px] font-mono-tab text-slate-500 mt-0.5">ID: {c.partner_slug}</div>
                      )}
                      {!partnerMode && (
                        <button
                          onClick={() => assignPartner(c)}
                          disabled={busy === c.id}
                          data-testid={`oc-assign-partner-${c.id}`}
                          className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-[var(--al-purple)] hover:underline disabled:opacity-50"
                        >
                          <Link2 className="h-3 w-3" /> {c.partner_slug ? "Partner ändern" : "Partner zuweisen"}
                        </button>
                      )}
                    </div>

                    <div className="lg:col-span-2">
                      <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Fortschritt</div>
                      <div className="mt-1 flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${allDone ? "bg-emerald-500" : "al-grad-bg"}`} style={{ width: `${Math.min(100, (total / TOTAL_CHECKS) * 100)}%` }} />
                        </div>
                        <div className="text-xs font-mono-tab font-bold text-slate-700">{total}/{TOTAL_CHECKS}</div>
                      </div>
                      {/* MT5 inline editor */}
                      {editingMt5 === c.id ? (
                        <div className="mt-1.5 flex items-center gap-1.5" data-testid={`oc-mt5-editor-${c.id}`}>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={mt5Draft}
                            onChange={(e) => setMt5Draft(e.target.value.replace(/[^0-9]/g, ""))}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveMt5(c);
                              if (e.key === "Escape") setEditingMt5(null);
                            }}
                            data-testid={`oc-mt5-input-${c.id}`}
                            placeholder="MT5 #"
                            autoFocus
                            className="font-mono-tab text-xs px-2 py-1 rounded border border-violet-200 focus:outline-none focus:border-[var(--al-purple)] w-32"
                          />
                          <button
                            onClick={() => saveMt5(c)}
                            disabled={busy === c.id}
                            data-testid={`oc-mt5-save-${c.id}`}
                            className="p-1 rounded text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                            title="Speichern (Enter)"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => setEditingMt5(null)}
                            data-testid={`oc-mt5-cancel-${c.id}`}
                            className="p-1 rounded text-slate-500 hover:bg-slate-100"
                            title="Abbrechen (Esc)"
                          >
                            <XIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setMt5Draft(c.mt5_account || "");
                            setEditingMt5(c.id);
                          }}
                          data-testid={`oc-mt5-edit-${c.id}`}
                          className="mt-1 inline-flex items-center gap-1 text-[11px] font-mono-tab text-slate-500 hover:text-[var(--al-purple)] group"
                          title="MT5-Nummer bearbeiten"
                        >
                          <span>MT5: {c.mt5_account || <span className="text-slate-400 italic">— nicht gesetzt —</span>}</span>
                          <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      )}
                    </div>

                    <div className="lg:col-span-3 flex flex-wrap gap-2 justify-end items-center">
                      {c.status === "pending" && (
                        <>
                          <button onClick={() => approve(c)} disabled={busy === c.id} data-testid={`oc-approve-${c.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-all disabled:opacity-50">
                            <CheckCircle2 className="h-4 w-4" /> Freigeben
                          </button>
                          <button onClick={() => reject(c)} disabled={busy === c.id} data-testid={`oc-reject-${c.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 text-sm font-semibold transition-all disabled:opacity-50">
                            <XCircle className="h-4 w-4" /> Ablehnen
                          </button>
                        </>
                      )}
                      {c.status === "approved" && allDone && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold uppercase tracking-widest">
                          <Sparkles className="h-3 w-3" /> {c.skip_onboarding ? "Skip aktiv" : "Onboarding fertig"}
                        </span>
                      )}
                      <button
                        onClick={() => toggleSkipOnboarding(c)}
                        disabled={busy === c.id}
                        data-testid={`oc-skip-${c.id}`}
                        title={c.skip_onboarding ? "Onboarding-Skip aufheben (Checkliste wieder anzeigen)" : "Onboarding überspringen (bereits auf RoboForex onboardet)"}
                        className={`p-2 rounded-md transition-colors disabled:opacity-50 ${c.skip_onboarding ? "bg-violet-100 text-[var(--al-purple)] hover:bg-violet-200" : "hover:bg-violet-50 text-violet-500"}`}
                      >
                        <FastForward className="h-4 w-4" />
                      </button>
                      <button onClick={() => triggerPasswordReset(c)} disabled={busy === c.id} data-testid={`oc-pw-reset-${c.id}`} title="Passwort-Reset-Mail an Kunden senden (72h-Link)"
                        className="p-2 rounded-md hover:bg-violet-50 text-violet-500 transition-colors disabled:opacity-50">
                        <KeyRound className="h-4 w-4" />
                      </button>
                      <button onClick={() => setPasswordDirect(c)} disabled={busy === c.id} data-testid={`oc-pw-set-${c.id}`} title="Passwort DIREKT setzen (Fallback wenn Mail-Link nicht funktioniert)"
                        className="p-2 rounded-md hover:bg-fuchsia-50 text-fuchsia-600 transition-colors disabled:opacity-50">
                        <Lock className="h-4 w-4" />
                      </button>
                      <button onClick={() => diagnoseAndRepair(c)} disabled={busy === c.id} data-testid={`oc-diagnose-${c.id}`} title="Login-Diagnose + Datenreparatur (saniert datetime/NaN-Felder die Login-500 verursachen)"
                        className="p-2 rounded-md hover:bg-amber-50 text-amber-600 transition-colors disabled:opacity-50">
                        <Stethoscope className="h-4 w-4" />
                      </button>
                      <button onClick={() => resetMyFxBook(c)} disabled={busy === c.id} data-testid={`oc-myfxbook-reset-${c.id}`} title="MyFxBook-Verbindung trennen + synchronisierte Daten löschen (Kunde muss neu verbinden)"
                        className="p-2 rounded-md hover:bg-rose-50 text-rose-500 transition-colors disabled:opacity-50">
                        <Unplug className="h-4 w-4" />
                      </button>
                      <button onClick={() => resetProgress(c)} disabled={busy === c.id} data-testid={`oc-reset-${c.id}`} title="Onboarding-Fortschritt zurücksetzen (Schema-Reset)"
                        className="p-2 rounded-md hover:bg-amber-50 text-amber-600 transition-colors disabled:opacity-50">
                        <RefreshCw className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(c)} disabled={busy === c.id} data-testid={`oc-delete-${c.id}`} title="Kunde löschen"
                        className="p-2 rounded-md hover:bg-rose-50 text-rose-500 transition-colors disabled:opacity-50">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      <MergeCustomersModal
        modal={mergeModal}
        busy={mergeBusy}
        primaryId={mergePrimaryId}
        onPickPrimary={setMergePrimaryId}
        onCancel={() => setMergeModal(null)}
        onConfirm={runMerge}
      />
    </div>
  );
}
